/* iter55z-audit — COGS Analyzer + Tier Pricing Editor.
 *
 * Mounted under Admin → Financials → COGS Analyzer.
 *
 * Two panels stacked:
 *   1. Cost-input sliders (LLM $/M, Copyscape $/scan, Stripe fees,
 *      editor labor, target margin) — dispatch to `/simulate` on
 *      change so the P&L table below refreshes without persisting.
 *   2. P&L table with margin badges (@ cap / @ 50% / @ 30%) and an
 *      "Apply suggestion" button per tier that writes the suggested
 *      LLM cap through `/tier-pricing` PUT.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Save, Calculator, Wand2, AlertTriangle,
} from 'lucide-react';
import { PanelHeader } from './_shared';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

function fmtUsd(v) {
  if (v == null) return '—';
  const s = v < 0 ? '−' : '';
  return `${s}$${Math.abs(v).toFixed(2)}`;
}

function marginBadge(pct) {
  if (pct == null) return null;
  const cls = pct < 15 ? 'bg-destructive/15 text-destructive'
            : pct < 30 ? 'bg-warning/15 text-warning'
            : 'bg-success/15 text-success';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}
          data-testid={`cogs-margin-badge-${pct.toFixed(1)}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

function Slider({ label, value, min, max, step, onChange, unit = '', testid }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{label}</span>
        <span className="font-mono text-foreground" data-testid={`${testid}-value`}>
          {typeof value === 'number' ? value.toFixed(step < 1 ? 2 : 0) : value}{unit}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step}
        value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="h-1.5 w-full appearance-none rounded-full bg-secondary accent-primary"
      />
    </div>
  );
}

export default function CogsAnalyzerPanel() {
  const [inputs, setInputs] = useState({
    // Feb-2026 COGS audit blended rate: 60% Sonnet ($3/$15) + 25% Haiku ($0.80/$4)
    // + 12% GPT-4o ($2.5/$10) + 3% Gemini Flash ($0.075/$0.30) at 3:1 in:out ratio = ~$4.10/M
    llm_cost_per_million: 4.10,
    copyscape_scan_usd:   0.03,
    stripe_pct:           2.9,
    stripe_flat_usd:      0.30,
    editor_labor_usd:     40,
    audiobook_sample_cost_usd: 0.75,
    target_margin_pct:    40,
  });
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [saving, setSaving] = useState(false);

  const simulate = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.post('/admin/financials/cogs-analyzer/simulate', {
        ...inputs, overrides: {},
      });
      setData(r.data);
    } catch (e) {
      toast.error(`Simulation failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }, [inputs]);

  useEffect(() => { simulate(); }, [simulate]);

  const applySuggestion = async (tier) => {
    const suggested = tier.suggestion?.suggested_llm_cap_cents;
    if (!suggested) return toast.warning('No attainable cap for that target — trim editor labor first.');
    setSaving(true);
    try {
      // Fetch current plans, patch this tier's cap, save.
      const cur = (await http.get('/admin/financials/tier-pricing')).data.plans || [];
      const next = cur.map((p) => (p.id === tier.tier_id
        ? {
            ...p,
            numeric_caps: {
              ...(p.numeric_caps || {}),
              max_llm_spend_cents: suggested,
            },
          }
        : p));
      await http.put('/admin/financials/tier-pricing', { plans: next });
      toast.success(`Applied: ${tier.name} LLM cap → $${(suggested / 100).toFixed(2)}`);
      await simulate();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  const rows = data?.tiers || [];

  return (
    <section data-testid="admin-cogs-analyzer-panel" className="space-y-4">
      <PanelHeader
        title="COGS Analyzer & Tier Pricing"
        subtitle="Adjust cost inputs to see real-time gross margin per tier. Click 'Apply suggestion' to persist a recommended LLM cap that hits your target margin."
        onRefresh={simulate}
      />

      {/* Cost inputs */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <Calculator className="h-3.5 w-3.5" /> Cost inputs
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Slider testid="cogs-input-llm"      label="LLM cost / 1M tokens"
                  value={inputs.llm_cost_per_million} min={1} max={40} step={0.01}
                  onChange={(v) => setInputs((s) => ({ ...s, llm_cost_per_million: v }))} unit=" USD" />
          <Slider testid="cogs-input-copyscape" label="Copyscape / scan"
                  value={inputs.copyscape_scan_usd} min={0.01} max={0.20} step={0.005}
                  onChange={(v) => setInputs((s) => ({ ...s, copyscape_scan_usd: v }))} unit=" USD" />
          <Slider testid="cogs-input-editor" label="Premier editor labor / month"
                  value={inputs.editor_labor_usd} min={0} max={200} step={5}
                  onChange={(v) => setInputs((s) => ({ ...s, editor_labor_usd: v }))} unit=" USD" />
          <Slider testid="cogs-input-audiobook" label="Audiobook sample / free run"
                  value={inputs.audiobook_sample_cost_usd} min={0} max={15} step={0.05}
                  onChange={(v) => setInputs((s) => ({ ...s, audiobook_sample_cost_usd: v }))} unit=" USD" />
          <Slider testid="cogs-input-stripe-pct" label="Stripe fee %"
                  value={inputs.stripe_pct} min={0} max={5} step={0.1}
                  onChange={(v) => setInputs((s) => ({ ...s, stripe_pct: v }))} unit=" %" />
          <Slider testid="cogs-input-stripe-flat" label="Stripe flat / charge"
                  value={inputs.stripe_flat_usd} min={0} max={1} step={0.05}
                  onChange={(v) => setInputs((s) => ({ ...s, stripe_flat_usd: v }))} unit=" USD" />
          <Slider testid="cogs-input-target"  label="Target gross margin"
                  value={inputs.target_margin_pct} min={20} max={80} step={1}
                  onChange={(v) => setInputs((s) => ({ ...s, target_margin_pct: v }))} unit=" %" />
        </div>
      </div>

      {/* P&L table */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Per-tier gross margin
          </div>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="cogs-pnl-table">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">
                <th className="py-2 pr-3">Tier</th>
                <th className="py-2 pr-3 text-right">Price</th>
                <th className="py-2 pr-3 text-right">LLM cap</th>
                <th className="py-2 pr-3 text-right">Copyscape</th>
                <th className="py-2 pr-3 text-right">Audiobook</th>
                <th className="py-2 pr-3 text-right">COGS @ cap</th>
                <th className="py-2 pr-3 text-right">Margin @ cap</th>
                <th className="py-2 pr-3 text-right">@ 50%</th>
                <th className="py-2 pr-3 text-right">@ 30%</th>
                <th className="py-2 pr-3 text-right">Suggest</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tier_id} className="border-b border-border/60" data-testid={`cogs-row-${r.tier_id}`}>
                  <td className="py-2 pr-3 font-semibold text-foreground">{r.name}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtUsd(r.price_usd)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtUsd(r.llm_cap_usd)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{r.copyscape_scans}× ({fmtUsd(r.cogs.copyscape)})</td>
                  <td className="py-2 pr-3 text-right font-mono">{r.audiobook_runs}× ({fmtUsd(r.cogs.audiobook)})</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtUsd(r.cogs.total)}</td>
                  <td className="py-2 pr-3 text-right">{marginBadge(r.margin_pct)}</td>
                  <td className="py-2 pr-3 text-right">{marginBadge(r.scenarios?.at_50pct?.margin_pct)}</td>
                  <td className="py-2 pr-3 text-right">{marginBadge(r.scenarios?.at_30pct?.margin_pct)}</td>
                  <td className="py-2 pr-3 text-right">
                    {r.suggestion?.hits_target_at_cap ? (
                      <span className="text-[11px] text-success">✓ at target</span>
                    ) : r.suggestion?.suggested_llm_cap_cents ? (
                      <button
                        disabled={saving}
                        onClick={() => applySuggestion(r)}
                        data-testid={`cogs-apply-suggestion-${r.tier_id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                      >
                        <Wand2 className="h-3 w-3" /> Cap → ${(r.suggestion.suggested_llm_cap_cents / 100).toFixed(2)}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-destructive" title={r.suggestion?.onboarding_hint || ''}>
                        <AlertTriangle className="h-3 w-3" /> Unreachable
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Trial onboarding hint */}
        {rows[0]?.suggestion?.onboarding_hint && (
          <div className="mt-4 rounded-md border border-warning/40 bg-warning/5 p-3 text-[11.5px] text-warning" data-testid="cogs-onboarding-hint">
            <strong>Trial-CAC hint:</strong> {rows[0].suggestion.onboarding_hint}
          </div>
        )}
      </div>
    </section>
  );
}
